/**
 * Cross-platform scroll utilities with iOS Safari and Capacitor compatibility.
 * iOS Safari and Capacitor sometimes ignore standard window.scrollTo calls.
 * On iOS with Capacitor, the #root element is the scrollable container.
 */

/**
 * Detects if we're running on iOS (Safari or Capacitor)
 */
function isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Gets the actual scroll container element for scrolling to specific positions.
 * On small screens (e.g. mobile), #root may be the scroll container via CSS (e.g. overflow-y: auto).
 * We use #root whenever it is scrollable so that scroll-to-element works on both iOS and Android.
 */
function getScrollContainer(): HTMLElement | null {
    const root = document.getElementById('root');
    if (root) {
        const style = window.getComputedStyle(root);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
            return root;
        }
    }
    return null;
}

/**
 * Performs the actual scroll to top operation
 */
function performScrollToTop(behavior: 'instant' | 'smooth'): void {
    // When #root (or another element) is the scroll container (e.g. small screens / mobile), scroll it first
    const scrollContainer = getScrollContainer();
    if (scrollContainer) {
        if (behavior === 'smooth') {
            scrollContainer.scrollTo?.({ top: 0, behavior: 'smooth' });
        }
        scrollContainer.scrollTop = 0;
    }

    // Standard window scroll
    try {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior
        });
    } catch {
        window.scrollTo(0, 0);
    }
    
    // Additional fallbacks for edge cases
    if (document.documentElement) {
        document.documentElement.scrollTop = 0;
    }
    if (document.body) {
        document.body.scrollTop = 0;
    }
}

/**
 * Scrolls to the top of the page using multiple fallback methods for iOS compatibility.
 * @param behavior - 'instant' for immediate scroll, 'smooth' for animated scroll
 */
export function scrollToTop(behavior: 'instant' | 'smooth' = 'instant'): void {
    // Perform scroll immediately
    performScrollToTop(behavior);
    
    // On iOS, also schedule scroll after next frame to handle React re-render timing
    if (isIOS()) {
        requestAnimationFrame(() => {
            performScrollToTop(behavior);
        });
    }
}

/**
 * Scrolls to a specific position on the page with iOS compatibility.
 * @param top - The vertical position to scroll to
 * @param behavior - 'instant' for immediate scroll, 'smooth' for animated scroll
 */
export function scrollToPosition(top: number, behavior: 'instant' | 'smooth' = 'instant'): void {
    const safeTop = Math.max(0, top);
    const scrollContainer = getScrollContainer();
    
    if (scrollContainer) {
        // iOS Capacitor
        if (behavior === 'smooth') {
            scrollContainer.scrollTo({ top: safeTop, behavior: 'smooth' });
        } else {
            scrollContainer.scrollTop = safeTop;
        }
    } else {
        // Standard window scroll
        try {
            window.scrollTo({
                top: safeTop,
                left: 0,
                behavior
            });
        } catch {
            window.scrollTo(0, safeTop);
        }
        
        if (document.documentElement) {
            document.documentElement.scrollTop = safeTop;
        }
        if (document.body) {
            document.body.scrollTop = safeTop;
        }
    }
}

/**
 * Gets the current scroll position from the correct container
 */
function getCurrentScrollTop(): number {
    const scrollContainer = getScrollContainer();
    
    if (scrollContainer) {
        return scrollContainer.scrollTop;
    }
    
    return window.pageYOffset || 
        document.documentElement?.scrollTop || 
        document.body?.scrollTop || 
        0;
}

/**
 * Scrolls an element into view with iOS compatibility, accounting for a fixed header.
 * Positions the TOP of the element at the top of the viewport (below the header).
 * Uses native scrollIntoView so the browser picks the correct scroll container on all viewport sizes
 * (window, #root, or any inner scrollable container).
 * @param element - The HTML element to scroll into view
 * @param options - Scroll options
 */
export function scrollElementIntoView(
    element: HTMLElement | null,
    options: {
        behavior?: 'instant' | 'smooth';
        headerOffset?: number;
        additionalOffset?: number;
    } = {}
): boolean {
    if (!element) return false;
    
    const { behavior = 'smooth', headerOffset, additionalOffset = 20 } = options;
    
    // Get header height if not provided
    const header = document.getElementById('app-header');
    const finalHeaderOffset = headerOffset ?? (header ? header.offsetHeight : 0);
    const totalOffset = finalHeaderOffset + additionalOffset;
    
    // Use native scrollIntoView on all viewport sizes so the browser picks the right scroll container
    const prevScrollMargin = element.style.scrollMarginTop;
    element.style.scrollMarginTop = `${totalOffset}px`;
    try {
        element.scrollIntoView({
            behavior: behavior === 'smooth' ? 'smooth' : 'instant',
            block: 'start',
            inline: 'nearest'
        });
    } finally {
        element.style.scrollMarginTop = prevScrollMargin;
    }
    return true;
}

/**
 * Gets the current scroll position with cross-browser compatibility.
 */
export function getScrollPosition(): number {
    return getCurrentScrollTop();
}
